/**
 * AdhocCapitalSection Component
 *
 * Renders one of two sibling tables sourced from the same adhocCapitalEvents
 * context state. The mode prop selects which subset of rows to show and the
 * UI affordances appropriate to that subset:
 *
 *   mode="capital"  - withdrawals and injections (affect the schedule).
 *   mode="accrual"  - interest accruals carried over from a prior contract
 *                     (informational on the schedule, paid out via the first
 *                     Adhoc Interest Payment on/after the accrual date, or
 *                     rolled into the maturity payment if none).
 *
 * Update/remove operations index into the FULL adhocCapitalEvents array
 * (translated from the filtered slice), so the existing handlers don't need
 * to change beyond the optional event_type override on add.
 *
 * Read-only for non-Draft contracts (Active/Approved/Matured).
 */

import React from 'react';
import {
  Box,
  TextField,
  IconButton,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useContractFormContext } from '../ContractFormContext';
import { formatNumber } from '../../../../utils/formatters';

const CAPITAL_TYPES = ['withdrawal', 'injection'];

function AdhocCapitalSection({ mode = 'capital' }) {
  const {
    adhocCapitalEvents,
    handleAddAdhocCapitalEvent,
    handleUpdateAdhocCapitalEvent,
    handleRemoveAdhocCapitalEvent,
    initialData,
  } = useContractFormContext();

  const isReadOnly = initialData?.status && initialData.status !== 'Draft';
  const isAccrual = mode === 'accrual';

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  // Filter the full event list to just this section's rows, but keep a pointer
  // back to the real index in the full array so update/remove handlers still
  // address the right row.
  const visibleRows = adhocCapitalEvents
    .map((event, realIdx) => ({ event, realIdx }))
    .filter(({ event }) => isAccrual
      ? event.event_type === 'interest_accrual'
      : CAPITAL_TYPES.includes(event.event_type || 'withdrawal'));

  const heading = isAccrual ? 'Adhoc Interest Accruals' : 'Scheduled Capital Movements';
  const addLabel = isAccrual ? 'Add Accrual' : 'Add Event';
  const addType = isAccrual ? 'interest_accrual' : 'withdrawal';

  const description = isAccrual
    ? 'Carry over interest accrued on a prior contract. The captured amount is paid out via the first Adhoc Interest Payment on/after this date (or rolled into the final maturity payment if none). Positive adds, negative reduces. Does not affect calculated interest on this schedule.'
    : 'Add planned capital withdrawals or injections that will affect the amortization schedule. These events are processed on their scheduled dates.';

  const readOnlyDescription = isAccrual
    ? 'Interest accruals carried over to this contract.'
    : 'Capital events on this contract. Additional changes can be made via the Capital Redemption action button above.';

  const emptyText = isAccrual
    ? (isReadOnly ? 'No interest accruals carried over.' : 'No interest accruals. Click "Add Accrual" to add one.')
    : (isReadOnly ? 'No scheduled capital movements on this contract.' : 'No scheduled capital movements. Click "Add Event" to add one.');

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{heading}</Typography>
        {!isReadOnly && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleAddAdhocCapitalEvent(addType)}
            size="small"
          >
            {addLabel}
          </Button>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isReadOnly ? readOnlyDescription : description}
      </Typography>

      {visibleRows.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography color="text.secondary">{emptyText}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell width={isAccrual ? '25%' : '20%'}>Date</TableCell>
                {!isAccrual && <TableCell width="15%">Type</TableCell>}
                <TableCell width={isAccrual ? '25%' : '20%'}>Amount</TableCell>
                <TableCell width={isAccrual ? '40%' : '30%'}>Description</TableCell>
                {isReadOnly && <TableCell width="10%">Source</TableCell>}
                {!isReadOnly && <TableCell width="5%" align="center">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map(({ event, realIdx }) => (
                <TableRow key={event.id || realIdx}>
                  <TableCell>
                    {isReadOnly ? (
                      <Typography variant="body2">{formatDate(event.event_date)}</Typography>
                    ) : (
                      <TextField
                        type="date"
                        value={event.event_date || ''}
                        onChange={(e) => handleUpdateAdhocCapitalEvent(realIdx, 'event_date', e.target.value)}
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  </TableCell>
                  {!isAccrual && (
                    <TableCell>
                      {isReadOnly ? (
                        <Chip
                          label={event.event_type === 'injection' ? 'Injection' : 'Withdrawal'}
                          size="small"
                          color={event.event_type === 'injection' ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      ) : (
                        <TextField
                          select
                          SelectProps={{ native: true }}
                          value={event.event_type || 'withdrawal'}
                          onChange={(e) => handleUpdateAdhocCapitalEvent(realIdx, 'event_type', e.target.value)}
                          size="small"
                          fullWidth
                        >
                          <option value="withdrawal">Withdrawal</option>
                          <option value="injection">Injection</option>
                        </TextField>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    {isReadOnly ? (
                      <Typography variant="body2">
                        {formatNumber(parseFloat(event.amount || 0), 2)}
                      </Typography>
                    ) : (
                      <TextField
                        type="number"
                        value={event.amount || ''}
                        onChange={(e) => handleUpdateAdhocCapitalEvent(realIdx, 'amount', e.target.value)}
                        size="small"
                        fullWidth
                        inputProps={isAccrual ? { step: 0.01 } : { step: 0.01, min: 0 }}
                        placeholder={isAccrual ? '0.00 (+/-)' : '0.00'}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {isReadOnly ? (
                      <Typography variant="body2" color="text.secondary">{event.description || '-'}</Typography>
                    ) : (
                      <TextField
                        value={event.description || ''}
                        onChange={(e) => handleUpdateAdhocCapitalEvent(realIdx, 'description', e.target.value)}
                        size="small"
                        fullWidth
                        placeholder="Optional note"
                      />
                    )}
                  </TableCell>
                  {isReadOnly && (
                    <TableCell>
                      <Chip
                        label={event.amendment_source === 'amendment' ? 'Amendment' : 'Draft'}
                        size="small"
                        variant="outlined"
                        color={event.amendment_source === 'amendment' ? 'info' : 'default'}
                      />
                    </TableCell>
                  )}
                  {!isReadOnly && (
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveAdhocCapitalEvent(realIdx)}
                        title="Remove"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!isReadOnly && visibleRows.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Note: Entries without a date or amount will be ignored when generating the schedule.
        </Typography>
      )}
    </Box>
  );
}

export default AdhocCapitalSection;
